import { Book, BookCopy, CheckCircle2, ChevronDown, ChevronRight, FileText, Home, Paperclip, Search, Star, Tag, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { isWordStartTerm } from "@engram/core/search";
import { db } from "../db/client";

/*
 * Evernote's search overlay (sidebar Search, Ctrl+Q): jump chips, "Search in" scope, recent and saved searches,
 * and recent notes by day; typing lists the best matches (title hits first). Enter runs the full search, or opens
 * the highlighted match.
 */

interface Hit {
  kind: "note" | "notebook" | "stack" | "tag" | "search";
  id: string;
  label: string;
  hint?: string;
  group: string;
}

const ICONS = { note: FileText, notebook: Book, stack: BookCopy, tag: Tag, search: Search };

function href(h: Hit): string {
  switch (h.kind) {
    case "note":
      return `/note/${h.id}`;
    case "notebook":
      return `/notebook/${h.id}`;
    case "stack":
      return `/stack/${h.id}`;
    case "tag":
      return `/tag/${h.id}`;
    case "search":
      return `/search?saved=${h.id}`;
  }
}

const RECENT_KEY = "engram.recentSearches";

export function recentSearches(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

export function rememberSearch(q: string) {
  const v = q.trim();
  if (!v) return;
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([v, ...recentSearches().filter((x) => x !== v)].slice(0, 8)));
  } catch {
    /* storage unavailable */
  }
}

const DAY = 86400000;
function dayGroup(ms: number): string {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const t = start.getTime();
  return ms >= t ? "Today" : ms >= t - DAY ? "Yesterday" : ms >= t - 6 * DAY ? "Past 7 days" : ms >= t - 29 * DAY ? "Past 30 days" : "Earlier";
}

const likeOf = (term: string) => `%${term.replace(/[\\%_]/g, (c) => "\\" + c)}%`;

async function find(q: string): Promise<Hit[]> {
  const term = q.trim().normalize("NFC");
  if (!term) {
    const notes = await db.query<{ id: string; title: string; nb: string | null; updated: number }>(
      `SELECT n.id, n.title, nb.name AS nb, n.updated FROM notes n LEFT JOIN notebooks nb ON nb.id = n.notebook_id
       WHERE n.deleted = 0 AND n.trashed_at IS NULL ORDER BY n.updated DESC LIMIT 12`,
    );
    return notes.map((n) => ({ kind: "note", id: n.id, label: n.title || "Untitled", hint: n.nb ?? undefined, group: dayGroup(n.updated) }));
  }
  const l = likeOf(term);
  // Like the full search: Latin terms must start a word ("gio" finds "Gio", not "region").
  const word = isWordStartTerm(term);
  const hit = (col: string) => (word ? `(${col} LIKE ?1 ESCAPE '\\' AND engram_word(${col}, ?2))` : `${col} LIKE ?1 ESCAPE '\\'`);
  const [notes, books, tags, searches] = await Promise.all([
    db.query<{ id: string; title: string; nb: string | null }>(
      `SELECT n.id, n.title, nb.name AS nb FROM notes n LEFT JOIN notebooks nb ON nb.id = n.notebook_id
       WHERE n.deleted = 0 AND n.trashed_at IS NULL AND (${hit("n.title")} OR ${hit("n.body_text")})
       ORDER BY ${hit("n.title")} DESC, n.updated DESC LIMIT 12`,
      word ? [l, term] : [l],
    ),
    db.query<{ id: string; name: string; kind: "stack" | "notebook" }>(
      "SELECT id, name, kind FROM notebooks WHERE deleted = 0 AND name LIKE ? ESCAPE '\\' ORDER BY name COLLATE NOCASE LIMIT 5",
      [l],
    ),
    db.query<{ id: string; name: string }>("SELECT id, name FROM tags WHERE deleted = 0 AND name LIKE ? ESCAPE '\\' ORDER BY name COLLATE NOCASE LIMIT 5", [l]),
    db.query<{ id: string; name: string }>("SELECT id, name FROM saved_searches WHERE deleted = 0 AND name LIKE ? ESCAPE '\\' LIMIT 5", [l]),
  ]);
  return [
    ...notes.map((n) => ({ kind: "note" as const, id: n.id, label: n.title || "Untitled", hint: n.nb ?? undefined, group: "Best matches" })),
    ...books.map((b) => ({ kind: b.kind, id: b.id, label: b.name, hint: b.kind === "stack" ? "Stack" : "Notebook", group: "Notebooks" })),
    ...tags.map((t) => ({ kind: "tag" as const, id: t.id, label: t.name, group: "Tags" })),
    ...searches.map((s) => ({ kind: "search" as const, id: s.id, label: s.name, group: "Saved searches" })),
  ];
}

interface Scope {
  label: string;
  query: string;
  icon: ReactNode;
}

/** The notebook, stack or tag list the overlay was opened from, for "Search in". */
function useScope(): Scope | null {
  const [path] = useLocation();
  const [scope, setScope] = useState<Scope | null>(null);
  useEffect(() => {
    const m = /^\/(notebook|stack|tag)\/([^/?]+)/.exec(path);
    if (!m) return setScope(null);
    const [, kind, id] = m;
    const sql = kind === "tag" ? "SELECT name FROM tags WHERE id = ?" : "SELECT name FROM notebooks WHERE id = ?";
    void db.get<{ name: string }>(sql, [id]).then((r) => {
      if (!r) return setScope(null);
      const quoted = /[\s"]/.test(r.name) ? `"${r.name.replace(/"/g, "")}"` : r.name;
      setScope({ label: r.name, query: `${kind}:${quoted}`, icon: kind === "tag" ? <Tag size={13} /> : kind === "stack" ? <BookCopy size={13} /> : <Book size={13} /> });
    });
  }, [path]);
  return scope;
}

const JUMPS: [string, string, ReactNode][] = [
  ["Home", "/home", <Home size={13} />],
  ["Shortcuts", "/shortcuts", <Star size={13} />],
  ["Notes", "/notes", <FileText size={13} />],
  ["Tasks", "/tasks", <CheckCircle2 size={13} />],
  ["Files", "/files", <Paperclip size={13} />],
  ["Notebooks", "/notebooks", <Book size={13} />],
  ["Trash", "/trash", <Trash2 size={13} />],
];

export function QuickSwitcher({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [hi, setHi] = useState(-1); // -1: the search box itself (Enter runs the full search)
  const [, navigate] = useLocation();
  const scope = useScope();
  const [inScope, setInScope] = useState(false);
  const [showRecent, setShowRecent] = useState(true);
  const recent = useMemo(() => recentSearches(), []);
  const [saved, setSaved] = useState<{ id: string; name: string }[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const term = q.trim();

  useEffect(() => {
    void db.query<{ id: string; name: string }>("SELECT id, name FROM saved_searches WHERE deleted = 0 ORDER BY name COLLATE NOCASE").then(setSaved);
  }, []);
  useEffect(() => {
    let alive = true;
    // A short pause while typing: each search scans every note's text, and one per keystroke queued up behind
    // each other in the database worker (the previous hits stay on screen meanwhile).
    const t = setTimeout(
      () =>
        void find(q).then((h) => {
          if (!alive) return;
          setHits(h);
          setHi(-1);
        }),
      q.trim() ? 60 : 0,
    );
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q]);
  useEffect(() => {
    listRef.current?.querySelector(".active")?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const go = (to: string) => {
    navigate(to);
    onClose();
  };
  const search = (text: string) => {
    const v = text.trim();
    if (!v) return;
    rememberSearch(v);
    go(`/search?q=${encodeURIComponent(inScope && scope ? `${scope.query} ${v}` : v)}`);
  };

  // Rows the arrow keys move through, in display order.
  const rows: { key: string; run: () => void }[] = [
    ...(!term && showRecent ? recent.map((r) => ({ key: `r:${r}`, run: () => search(r) })) : []),
    ...(!term ? saved.map((s) => ({ key: `s:${s.id}`, run: () => go(`/search?saved=${s.id}`) })) : []),
    ...hits.map((h) => ({ key: `${h.kind}:${h.id}`, run: () => go(href(h)) })),
  ];
  const item = (key: string, icon: ReactNode, label: ReactNode, hint?: string) => {
    const i = rows.findIndex((r) => r.key === key);
    return (
      <button key={key} className={`menu-item${i === hi ? " active" : ""}`} onMouseEnter={() => setHi(i)} onClick={() => rows[i]?.run()}>
        <span className="menu-icon">{icon}</span>
        <span className="menu-label">{label}</span>
        {hint && <span className="chip search-hint">{hint}</span>}
      </button>
    );
  };

  return createPortal(
    <div className="modal-backdrop top" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal quick search-overlay" role="dialog" aria-label="Search">
        <label className="quick-input">
          <Search size={16} />
          <input
            autoFocus
            placeholder="Search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            enterKeyHint="search"
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setHi((h) => Math.min(h + 1, rows.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHi((h) => Math.max(h - 1, -1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                if (hi >= 0) rows[hi]?.run();
                else search(q);
              } else if (e.key === "Escape") onClose();
            }}
          />
          {q && (
            <button className="icon-btn" aria-label="Clear search" onClick={() => setQ("")}>
              <X size={14} />
            </button>
          )}
        </label>
        {!term && (
          <div className="search-jumps">
            {JUMPS.map(([label, to, icon]) => (
              <button key={to} className="search-jump" onClick={() => go(to)}>
                {icon} {label}
              </button>
            ))}
          </div>
        )}
        <div className="quick-list" ref={listRef}>
          {scope && (
            <>
              <div className="menu-heading">Search in</div>
              <div className="search-scope">
                <button className={`scope-chip${!inScope ? " on" : ""}`} onClick={() => setInScope(false)}>Everywhere</button>
                <button className={`scope-chip${inScope ? " on" : ""}`} onClick={() => setInScope(true)}>
                  {scope.icon} {scope.label}
                </button>
              </div>
            </>
          )}
          {term && (
            <button className={`menu-item search-all${hi === -1 ? " active" : ""}`} onMouseEnter={() => setHi(-1)} onClick={() => search(q)}>
              <span className="menu-icon"><Search size={14} /></span>
              <span className="menu-label">
                Search notes for <strong>{term}</strong>
                {inScope && scope ? ` in ${scope.label}` : ""}
              </span>
              <span className="menu-hint">Enter</span>
            </button>
          )}
          {!term && recent.length > 0 && (
            <>
              <button className="menu-heading heading-toggle" onClick={() => setShowRecent((v) => !v)}>
                Recent searches {showRecent ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
              {showRecent && recent.map((r) => item(`r:${r}`, <Search size={14} />, r))}
            </>
          )}
          {!term && saved.length > 0 && (
            <>
              <div className="menu-heading">Saved searches</div>
              {saved.map((s) => item(`s:${s.id}`, <Search size={14} />, s.name))}
            </>
          )}
          {hits.map((h, i) => {
            const Icon = ICONS[h.kind];
            return [
              i === 0 || hits[i - 1]!.group !== h.group ? <div key={`g:${h.group}`} className="menu-heading">{h.group}</div> : null,
              item(`${h.kind}:${h.id}`, <Icon size={14} />, h.label, h.hint),
            ];
          })}
          {term && hits.length === 0 && <div className="menu-empty">No titles or notes contain "{term}"</div>}
        </div>
        <div className="search-foot">
          <span><kbd>↑</kbd> <kbd>↓</kbd> Select</span>
          <span><kbd>↵</kbd> Open</span>
          <span><kbd>Esc</kbd> Close</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Evernote's shortcuts, as they work here. Ctrl+N / Ctrl+Shift+N / Ctrl+T belong to Chrome, so the web app uses the alternates. */
const SHEET: [string, [string, string][]][] = [
  [
    "General",
    [
      ["New note", "Ctrl+Alt+N"],
      ["Today's note", "Ctrl+Alt+D"],
      ["New notebook", "Alt+Shift+N"],
      ["New tag", "Alt+Shift+T"],
      ["New task", "Alt+T"],
      ["Jump to…", "Ctrl+Q"],
      ["Search", "Ctrl+Alt+F"],
      ["Open shortcut 1–9", "Ctrl+1…9"],
      ["All notes / Notebooks / Tags", "Ctrl+Alt+1 / 2 / 3"],
      ["Back / Forward", "Ctrl+[ / Ctrl+]"],
      ["Show or hide sidebar", "F10"],
      ["Settings", "Ctrl+,"],
      ["Keyboard shortcuts", "Ctrl+/"],
    ],
  ],
  [
    "Note list",
    [
      ["Previous / next note", "↑ / ↓"],
      ["Focus the note list", "Ctrl+Alt+\\"],
      ["Edit the open note", "Enter"],
      ["Select several notes", "Ctrl+click, Shift+click"],
      ["Select all", "Ctrl+A"],
      ["Move to Trash", "Delete"],
    ],
  ],
  [
    "Note",
    [
      ["Rename (edit title)", "F2"],
      ["Move to notebook", "Ctrl+Shift+M"],
      ["Copy note link", "Ctrl+Alt+L"],
      ["Note info", "Ctrl+Alt+I"],
      ["Find in note", "Ctrl+F"],
      ["Find and replace", "Ctrl+H"],
      ["Find next / previous", "Ctrl+G / Ctrl+Shift+G"],
      ["Print", "Ctrl+P"],
    ],
  ],
  [
    "Formatting",
    [
      ["Bold / Italic / Underline", "Ctrl+B / I / U"],
      ["Strikethrough", "Ctrl+Shift+S"],
      ["Highlight", "Ctrl+Shift+H"],
      ["Large / Medium / Small header", "Ctrl+Alt+1 / 2 / 3"],
      ["Normal text", "Ctrl+Alt+0"],
      ["Bulleted / Numbered list", "Ctrl+Shift+B / O"],
      ["Checklist", "Ctrl+Shift+C"],
      ["Code block", "Ctrl+Shift+L"],
      ["Divider", "Ctrl+Shift+-"],
      ["Add / remove link", "Ctrl+K / Ctrl+Shift+K"],
      ["Link to note", "Ctrl+Alt+K"],
      ["Align left / center / right", "Ctrl+L / E / J"],
      ["Simplify formatting", "Ctrl+Space"],
      ["Insert date / time", "Alt+Shift+D / Ctrl+Shift+D"],
      ["Indent / Outdent", "Tab / Shift+Tab"],
      ["Paste as plain text", "Ctrl+Shift+V"],
    ],
  ],
];

export function KeyboardHelp({ onClose }: { onClose: () => void }) {
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal wide keys-help" onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <h2>Keyboard shortcuts</h2>
        <div className="keys-grid">
          {SHEET.map(([group, rows]) => (
            <section key={group}>
              <h3>{group}</h3>
              <dl>
                {rows.map(([label, keys]) => (
                  <div key={label} className="keys-row">
                    <dt>{label}</dt>
                    <dd><kbd>{keys}</kbd></dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
        <div className="modal-actions">
          <button className="btn" autoFocus onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
